import React from "react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { EmployeeAvatar } from "components/common/select/EmployeeBadge";
import { TABS } from "./common/usePaymentRequestQuery";
import { PriorityPill } from "./common/priority";
import { BANK_STATUS, DISBURSEMENT, Pill, StatusPill } from "./common/status";

export { PRIORITY_OPTIONS } from "./common/priority";
export { STATUS, STATUS_OPTIONS } from "./common/status";

/**
 * Cột của bảng danh sách Payment Request — một chỗ khai báo cho header (nhãn,
 * key sort phía server, độ rộng mặc định) và ô dữ liệu, để thêm/bớt cột theo tab
 * không phải sửa hai nơi.
 *
 * sortKey  = BaseController::SORT_EXPRESSIONS (allowlist phía BE).
 * tabs     = tab nào hiện cột này; bỏ trống = mọi tab.
 * sticky   = ghim bên trái khi cuộn ngang (mọi tab).
 */

// USD hiện 2 số lẻ, VND số nguyên — theo đơn vị tiền của phiếu.
const money = (v, currency = "VND") => (Number(v) || 0).toLocaleString("en-US", currency === "USD"
  ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
  : { maximumFractionDigits: 0 });
const initialsOf = (s) => (String(s || "?").trim().slice(0, 2) || "?").toUpperCase();
const dash = <span className="tw-text-geist-fg-3">—</span>;

// 3 loại phiếu — chọn ở dropdown "New request" để mở thẳng đúng loại.
export const FORM_TYPES = [
  { value: "qf01", label: "Payment", iso: "QF-FNC-01", desc: "With invoices", icon: "bi-receipt" },
  { value: "qf02", label: "Advance Payment", iso: "QF-FNC-02", desc: "No invoice needed", icon: "bi-wallet2" },
  { value: "qf03", label: "Clear Downpayment", iso: "QF-FNC-03", desc: "Settle an advance", icon: "bi-clipboard-check" },
];
export const FORM_TYPE_OPTIONS = FORM_TYPES.map((f) => ({
  value: f.value, label: `${f.label} · ${f.iso}`,
  icon: <i className={`bi ${f.icon} tw-text-[12px] tw-text-geist-fg-2`} aria-hidden="true" />,
}));

const statusIdOf = (a) => a.status?.id ?? a.status;

/**
 * Ô "người" (Prepared by / Waiting on): avatar chung của app (EmployeeAvatar —
 * cùng dấu nhận diện với PersonChip) + 2 dòng; tooltip mang đủ tên, email, chức danh.
 */
const PersonCell = ({ name, secondary, tooltip }) => (
  <div className="tw-flex tw-items-center tw-gap-2.5 tw-min-w-0" title={tooltip}>
    <EmployeeAvatar name={name} size={28} fontSize={10} />
    <div className="tw-min-w-0 tw-flex-1">
      <div className="tw-text-[13px] tw-font-medium tw-text-geist-fg tw-truncate">{name}</div>
      {secondary && <div className="tw-text-[11px] tw-text-geist-fg-3 tw-truncate">{secondary}</div>}
    </div>
  </div>
);

PersonCell.propTypes = {
  name: PropTypes.string,
  secondary: PropTypes.string,
  tooltip: PropTypes.string,
};

const tooltipOf = (...lines) => lines.filter(Boolean).join("\n");

// Ngân hàng chỉ có nghĩa sau khi duyệt xong (hoặc khi lệnh đã rời Portal).
const BankCell = ({ a }) => {
  const approved = statusIdOf(a) === 2;
  if (!approved && (!a.bank_status || a.bank_status === "not_sent")) return dash;

  const hsbc = a.bank_route === "hsbc";
  const state = hsbc ? BANK_STATUS[a.bank_status] : (DISBURSEMENT[a.disbursement_status] || BANK_STATUS[a.bank_status]);
  return (
    <div className="tw-min-w-0">
      {state ? <Pill tone={state}>{state.name}</Pill> : dash}
      <div className="tw-text-[11px] tw-text-geist-fg-3 tw-truncate tw-mt-0.5">
        {hsbc ? "HSBC" : a.bank_route === "non_hsbc" ? "Non-HSBC" : "Bank not set"}
        {a.bank_reference ? ` · ${a.bank_reference}` : ""}
      </div>
    </div>
  );
};

BankCell.propTypes = {
  // A payment-request list row.
  a: PropTypes.shape({
    bank_status: PropTypes.string,
    bank_route: PropTypes.string,
    bank_reference: PropTypes.string,
    disbursement_status: PropTypes.string,
  }).isRequired,
};

const WAITING_TABS = [TABS.MINE, TABS.ALL, TABS.INBOX];
const BANK_TABS = [TABS.MINE, TABS.ALL, TABS.APPROVED, TABS.UNPAID];
const DUE_TABS = [TABS.APPROVED, TABS.UNPAID];

export const COLUMNS = [
  {
    key: "request", label: "Request", sortKey: "code", width: 260, sticky: true,
    render: (a) => (
      <div className="tw-flex tw-items-center tw-gap-2.5 tw-min-w-0">
        <div className="tw-shrink-0 tw-w-9 tw-h-9 tw-rounded-xl tw-bg-geist-100 tw-text-geist-fg-2 tw-flex tw-items-center tw-justify-center tw-text-[11px] tw-font-semibold tw-uppercase">
          {initialsOf(a.payee)}
        </div>
        <div className="tw-min-w-0 tw-flex-1">
          {/* Link duy nhất của dòng (2026-10-25: bỏ click cả dòng) — <a> thật nên
              Ctrl/Cmd-click, chuột giữa, "Open in new tab" đều chạy. */}
          <Link to={`/payment-requests/${a.id}`} title={`Open ${a.code}`}
            className="tw-block tw-text-[13px] tw-font-semibold tw-text-geist-accent tw-no-underline hover:tw-underline focus-visible:tw-underline tw-truncate">
            {a.code}
          </Link>
          <div className="tw-text-[11px] tw-text-geist-fg-3 tw-truncate" title={a.payee || ""}>{a.payee || "—"}</div>
        </div>
      </div>
    ),
  },
  {
    key: "form_type", label: "Form type", sortKey: "form_type", width: 140,
    render: (a) => {
      const ft = FORM_TYPES.find((f) => f.value === a.form_type);
      return ft ? (
        <>
          <div className="tw-text-[13px] tw-text-geist-fg tw-truncate">{ft.label}</div>
          <div className="tw-text-[11px] tw-text-geist-fg-3 tw-font-geist-mono">{ft.iso}</div>
        </>
      ) : dash;
    },
  },
  {
    key: "department", label: "Department", sortKey: "department", width: 120,
    render: (a) => <div className="tw-text-[13px] tw-text-geist-fg-2 tw-truncate">{a.department || dash}</div>,
  },
  {
    key: "prepared_by", label: "Prepared by", sortKey: "requestor", width: 180,
    render: (a) => (a.requestor ? (
      <PersonCell
        name={a.requestor}
        secondary={a.created_date}
        tooltip={tooltipOf(
          a.requestor_full_name && a.requestor_full_name !== a.requestor ? `${a.requestor} — ${a.requestor_full_name}` : a.requestor,
          a.requestor_email,
          a.created_date && `Created ${a.created_date}`,
        )}
      />
    ) : dash),
  },
  {
    key: "waiting_on", label: "Waiting on", sortKey: "current_approver_email", width: 200, tabs: WAITING_TABS,
    render: (a) => (statusIdOf(a) === 1 && a.current_approver_email ? (
      <PersonCell
        name={a.current_approver_name || a.current_approver_email}
        secondary={a.current_step_label || "Signer"}
        tooltip={tooltipOf(
          a.current_approver_name || a.current_approver_email,
          a.current_approver_email,
          a.current_approver_position,
          `Step: ${a.current_step_label || "Signer"}`,
        )}
      />
    ) : dash),
  },
  {
    key: "due_date", label: "Due date", sortKey: "due_date", width: 110, tabs: DUE_TABS,
    render: (a) => <div className="tw-text-[13px] tw-text-geist-fg-2 tw-tabular-nums tw-whitespace-nowrap">{a.due_date || dash}</div>,
  },
  {
    key: "total", label: "Total", sortKey: "total_amount", defaultDir: "desc", width: 140, align: "right",
    render: (a) => {
      const currency = a.payment_currency || "VND";
      return (
        <div className="tw-text-right tw-tabular-nums tw-truncate" title={`${money(a.total_amount, currency)} ${currency}`}>
          <span className="tw-text-[13px] tw-font-semibold tw-text-geist-fg">{money(a.total_amount, currency)}</span>
          <span className="tw-ml-1 tw-text-[10px] tw-font-semibold tw-text-geist-fg-3">{currency}</span>
        </div>
      );
    },
  },
  {
    key: "priority", label: "Priority", sortKey: "priority", defaultDir: "desc", width: 110,
    render: (a) => <PriorityPill value={a.priority} />,
  },
  {
    key: "status", label: "Status", sortKey: "status", width: 120,
    render: (a) => <StatusPill status={statusIdOf(a)} />,
  },
  {
    key: "bank", label: "Payment", sortKey: "bank_status", width: 170, tabs: BANK_TABS,
    render: (a) => <BankCell a={a} />,
  },
];

export const columnsForTab = (tab) => COLUMNS.filter((c) => !c.tabs || c.tabs.includes(tab));

export const DEFAULT_WIDTHS = Object.fromEntries(COLUMNS.map((c) => [c.key, c.width]));
